import {Injectable} from '@angular/core';

const UTM_KEY: string = 'utm';
// separe la signature de l'app du utm_term d'origine (absent de tous les utm_term connus, sans encodage dans une URL)
const UTM_TERM_SEPARATOR: string = '~';
const mediumsData: {[key: string]: {rank: number, cookieLifeTime: number}} = {
  "apps": {rank: 1, cookieLifeTime: 86400 * 7},
  "sea": {rank: 2, cookieLifeTime: 86400 * 30},
  "reseaux_sociaux": {rank: 3, cookieLifeTime: 86400 * 30},
  // le medium des publicites Meta : absent de cette liste, il valait « unknown » (rang 6) et ne remplacait jamais une
  // ancienne UTM « reseaux_sociaux » — les gens revenus par une pub restaient attribues a l'ancienne pendant 30 jours
  "PaidSocial": {rank: 3, cookieLifeTime: 86400 * 30},
  "affiliation": {rank: 4, cookieLifeTime: 86400 * 30},
  "email": {rank: 5, cookieLifeTime: 86400 * 7},
  "unknown": {rank: 6, cookieLifeTime: 86400 * 30},
  "referer": {rank: 7, cookieLifeTime: 86400 * 30},
  "seo": {rank: 8, cookieLifeTime: 86400 * 30},
}

export interface UTMList {
  utmMedium: string;
  utmSource?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  utmId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UTMService {
  // DO NOT STORE DEFAULT UTM
  // Medium is "apps" so it would be never replaced
  private defaultUtm: UTMList = {
    utmMedium: 'apps',
    utmSource: 'bot_newgora',
  };

  constructor() {
  }

  getDefaultUtmList(): UTMList {
    const utmList = this.defaultUtm;
    return {...utmList, ...{utmCampaign: 'web'}};
  }

  getCurrentUtmList(fallback?: UTMList): UTMList | null {
    const rawData = localStorage.getItem(UTM_KEY);
    if (!rawData) {
      return fallback || null;
    }
    const utmList = JSON.parse(rawData);
    if (this.isExpired(utmList.expireAt)) {
      return fallback || null;
    }
    return utmList.list;
  }

  persistUtmList(utmList: UTMList, force: boolean = false): boolean {
    const mediumData = mediumsData[utmList.utmMedium] || mediumsData['unknown'];
    const expireAt = new Date(Date.now() + mediumData.cookieLifeTime * 1000);
    const currentUtmList = this.getCurrentUtmList();
    if (force || !currentUtmList) {
      localStorage.setItem(UTM_KEY, JSON.stringify({list: utmList, expireAt}));
      return true;
    }
    // check if new UTMs should replace older
    const oldMediumData = mediumsData[currentUtmList.utmMedium] || mediumsData['unknown'];
    if (mediumData.rank <= oldMediumData.rank) {
      localStorage.setItem(UTM_KEY, JSON.stringify({list: utmList, expireAt}));
      return true;
    }
    return false;
  }

  parseUtmListFromRelativeUrl(url: string): UTMList | null {
    const urlObject = new URL(url, 'http://dummy');
    return this.parseUtmListFromUrl(urlObject);
  }

  parseUtmListFromAbsoluteUrl(url: string): UTMList | null {
    const urlObject = new URL(url);
    return this.parseUtmListFromUrl(urlObject);
  }

  /**
   * Les UTM des liens d'Isabella vers Wengo (landings, liste d'experts) : celles de la campagne qui a amene le visiteur
   * (ou, a defaut, celles de l'app), pour que Wengo attribue le client a la campagne. Isabella s'y signe dans utm_term :
   * `isabella.<emplacement>`, suivi de `~` et du utm_term d'origine s'il y en a un (le nom de la pub Meta) — on coupe au
   * premier `~` pour retrouver l'un et l'autre. `placement` : d'ou part le lien (fin-cartes-2, fin-liste, limite-popup…).
   */
  appLinkUtm(placement: string): {[key: string]: string} {
    const utmList = this.getCurrentUtmList(this.getDefaultUtmList()) as UTMList;
    const signature = `isabella.${placement}`;
    return this.urlFormat({...utmList, utmTerm: utmList.utmTerm ? `${signature}${UTM_TERM_SEPARATOR}${utmList.utmTerm}` : signature});
  }

  urlFormat(utmList: UTMList) {
    const obj: {[key: string]: string} = {};
    if (utmList.utmMedium) {
      obj['utm_medium'] = utmList.utmMedium;
    }
    if (utmList.utmSource) {
      obj['utm_source'] = utmList.utmSource;
    }
    if (utmList.utmCampaign) {
      obj['utm_campaign'] = utmList.utmCampaign;
    }

    if (utmList.utmTerm) {
      obj['utm_term'] = utmList.utmTerm;
    }
    if (utmList.utmContent) {
      obj['utm_content'] = utmList.utmContent;
    }
    if (utmList.utmId) {
      obj['utm_id'] = utmList.utmId;
    }
    return obj;
  }

  private isExpired(date: Date): boolean {
    return date < new Date();
  }

  private parseUtmListFromUrl(url: URL): UTMList | null {
    const searchParams = url.searchParams;
    if (searchParams.get('utm_medium') === null && searchParams.get('utm_mediums') === null) {
      return null;
    }
    const utmList: UTMList = {
      utmMedium: (searchParams.get('utm_medium') || searchParams.get('utm_mediums')) as string
    };
    if (searchParams.get('utm_source') !== null) {
      utmList.utmSource = searchParams.get('utm_source') as string;
    }
    if (searchParams.get('utm_sources') !== null) {
      utmList.utmSource = searchParams.get('utm_sources') as string;
    }
    if (searchParams.get('utm_campaign') !== null) {
      utmList.utmCampaign = searchParams.get('utm_campaign') as string;
    }
    if (searchParams.get('utm_campaigns') !== null) {
      utmList.utmCampaign = searchParams.get('utm_campaigns') as string;
    }
    if (searchParams.get('utm_term') !== null) {
      utmList.utmTerm = searchParams.get('utm_term') as string;
    }
    if (searchParams.get('utm_terms') !== null) {
      utmList.utmTerm = searchParams.get('utm_terms') as string;
    }
    if (searchParams.get('utm_content') !== null) {
      utmList.utmContent = searchParams.get('utm_content') as string;
    }
    if (searchParams.get('utm_contents') !== null) {
      utmList.utmContent = searchParams.get('utm_contents') as string;
    }
    if (searchParams.get('utm_id') !== null) {
      utmList.utmId = searchParams.get('utm_id') as string;
    }
    if (searchParams.get('utm_ids') !== null) {
      utmList.utmId = searchParams.get('utm_ids') as string;
    }
    return utmList;
  }
}
